import { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { useDispatch, useSelector } from 'react-redux';
import SearchableDropdown from '../../../../components/SearchableDropdown';
import usePermissions from '../../../../hook/usePermissions';
import { PERMISSIONS } from '../../../../consts/permissions';
import {
    getVmsSettings,
    updateVmsSettings,
} from '../../../../redux/actions/SocietyPortal/VmsAction/VmsAction';
import { selectedSocietyStorer } from '../../../../utils/webStorer';
import { VmsLoader } from '../components/vms/VmsListShell';
import { labelCls, hintCls, controlCls } from '../components/form/formStyles';
import { GROUPS, fromApi, toApi } from './settingsSchema';

// How wide each kind of control needs to be. A minutes box holds two digits; a
// mode dropdown holds a sentence. Sizing them to their content stops the page
// reading as a column of identical full-width boxes.
const CONTROL_WIDTH = {
    number: 'tw-w-28',
    time: 'tw-w-36',
    enum: 'tw-w-full sm:tw-max-w-md',
    text: 'tw-w-full sm:tw-max-w-md',
};

const TONE = {
    emerald: { chip: 'tw-bg-emerald-50 tw-text-emerald-700 tw-border-emerald-100', icon: 'tw-text-emerald-600' },
    sky: { chip: 'tw-bg-sky-50 tw-text-sky-700 tw-border-sky-100', icon: 'tw-text-sky-600' },
    slate: { chip: 'tw-bg-slate-50 tw-text-slate-600 tw-border-slate-200', icon: 'tw-text-slate-500' },
};

const Toggle = ({ checked, onChange, disabled }) => (
    <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={`tw-relative tw-inline-flex tw-h-5 tw-w-9 tw-shrink-0 tw-rounded-full tw-transition-colors disabled:tw-opacity-50 ${checked ? 'tw-bg-sky-600' : 'tw-bg-gray-300'}`}
    >
        <span className={`tw-absolute tw-top-0.5 tw-h-4 tw-w-4 tw-rounded-full tw-bg-white tw-shadow tw-transition-transform ${checked ? 'tw-translate-x-4' : 'tw-translate-x-0.5'}`} />
    </button>
);

const VmsSettings = () => {
    const dispatch = useDispatch();
    const societyId = selectedSocietyStorer.getSocietyId();
    const { can } = usePermissions();
    const canEdit = can(PERMISSIONS.VMS_SETTINGS_UPDATE);

    const { data, loading, saving } = useSelector(state => state.VmsReducer?.settings) || {};
    const [form, setForm] = useState(null);

    // The GET creates the row with defaults on first read, so this never 404s
    // and the form always has a complete object to work from.
    useEffect(() => {
        if (societyId) dispatch(getVmsSettings({ societyId }));
    }, [dispatch, societyId]);

    useEffect(() => {
        if (data) setForm(fromApi(data));
    }, [data]);

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const handleSubmit = (ev) => {
        ev.preventDefault();
        dispatch(updateVmsSettings(toApi(form, societyId)));
    };

    if (loading && !form) {
        return (
            <div className="tw-p-5 tw-bg-gradient-to-br tw-from-gray-50 tw-to-slate-50 tw-min-h-screen">
                <div className="tw-py-16"><VmsLoader label="Loading settings..." /></div>
            </div>
        );
    }

    if (!form) {
        return (
            <div className="tw-p-5 tw-bg-gradient-to-br tw-from-gray-50 tw-to-slate-50 tw-min-h-screen">
                <div className="tw-max-w-md tw-mx-auto tw-mt-16 tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-p-8 tw-text-center">
                    <Icon icon="mdi:cog-off-outline" className="tw-text-4xl tw-text-gray-300" />
                    <h1 className="tw-mb-0 tw-text-sm tw-font-bold tw-text-gray-800 tw-mt-2">Settings unavailable</h1>
                    <p className="tw-mb-0 tw-text-[11px] tw-text-gray-500 tw-mt-1">
                        {societyId ? 'The settings could not be loaded.' : 'Pick a society first.'}
                    </p>
                </div>
            </div>
        );
    }

    const renderField = (field) => {
        const value = form[field.key];

        if (field.type === 'boolean') {
            return (
                <div key={field.key} className="tw-flex tw-items-start tw-justify-between tw-gap-4 tw-py-2.5 tw-border-b tw-border-gray-50 last:tw-border-0">
                    <div className="tw-min-w-0">
                        <p className="tw-mb-0 tw-text-xs tw-font-semibold tw-text-gray-800">{field.label}</p>
                        {field.hint && <p className="tw-mb-0 tw-text-[10px] tw-text-gray-500 tw-mt-0.5">{field.hint}</p>}
                    </div>
                    <Toggle checked={!!value} disabled={!canEdit} onChange={(v) => set(field.key, v)} />
                </div>
            );
        }

        return (
            <div key={field.key} className="tw-py-2.5 tw-border-b tw-border-gray-50 last:tw-border-0">
                <label className={labelCls}>{field.label}</label>
                {/* controlCls carries tw-w-full, which is right inside a grid
                    column but stretches a two-digit number across the whole card
                    on this full-width page. Each control is capped to the width
                    its content actually needs. */}
                {field.type === 'enum' ? (
                    <div className={CONTROL_WIDTH.enum}>
                        <SearchableDropdown
                            small
                            clearable={false}
                            disabled={!canEdit}
                            value={value || ''}
                            onChange={(v) => set(field.key, v)}
                            options={field.options}
                            placeholder="Select"
                        />
                    </div>
                ) : (
                    <div className="tw-flex tw-items-center tw-gap-2">
                        <div className={CONTROL_WIDTH[field.type] || CONTROL_WIDTH.number}>
                            <input
                                type={field.type === 'time' ? 'time' : 'number'}
                                min={field.min}
                                max={field.max}
                                disabled={!canEdit}
                                value={value ?? ''}
                                onChange={e => set(field.key, e.target.value)}
                                className={`${controlCls(false)} disabled:tw-bg-gray-50`}
                            />
                        </div>
                        {field.suffix && <span className="tw-text-[11px] tw-text-gray-500 tw-whitespace-nowrap">{field.suffix}</span>}
                    </div>
                )}
                {field.hint && <p className={hintCls}>{field.hint}</p>}
            </div>
        );
    };

    return (
        <div className="tw-p-5 tw-bg-gradient-to-br tw-from-gray-50 tw-to-slate-50 tw-min-h-screen">
            <form onSubmit={handleSubmit} className="tw-w-full tw-space-y-4">
                <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-p-4 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
                    <div>
                        <h1 className="tw-mb-0 tw-text-base tw-font-bold tw-text-gray-800">Visitor Management Settings</h1>
                        <p className="tw-mb-0 tw-text-[11px] tw-text-gray-500 tw-mt-0.5">
                            How your gate behaves — and which of these rules the system actually applies
                        </p>
                    </div>
                    {canEdit && (
                        <button
                            type="submit"
                            disabled={saving}
                            className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-text-xs tw-font-medium tw-text-white tw-bg-sky-600 hover:tw-bg-sky-700 tw-rounded-md disabled:tw-opacity-50"
                        >
                            <Icon icon="mdi:content-save-outline" className="tw-text-base" />
                            {saving ? 'Saving...' : 'Save Settings'}
                        </button>
                    )}
                </div>

                {!canEdit && (
                    <div className="tw-flex tw-items-start tw-gap-2 tw-px-3 tw-py-2 tw-rounded-lg tw-bg-slate-50 tw-border tw-border-slate-200 tw-text-[11px] tw-text-slate-700">
                        <Icon icon="mdi:eye-outline" className="tw-text-base tw-shrink-0 tw-mt-px" />
                        <span>You can view these settings but not change them.</span>
                    </div>
                )}

                {GROUPS.map(group => {
                    const tone = TONE[group.tone] || TONE.slate;
                    return (
                        <div key={group.id} className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-overflow-hidden">
                            <div className="tw-p-4 tw-border-b tw-border-gray-100">
                                <div className="tw-flex tw-items-center tw-gap-2">
                                    <Icon icon={group.icon} className={`tw-text-base ${tone.icon}`} />
                                    <h2 className="tw-mb-0 tw-text-xs tw-font-bold tw-text-gray-800">{group.title}</h2>
                                </div>
                                <p className={`tw-mb-0 tw-mt-2 tw-px-2.5 tw-py-1.5 tw-rounded-md tw-border tw-text-[10px] ${tone.chip}`}>
                                    {group.hint}
                                </p>
                            </div>
                            <div className="tw-px-4 tw-py-1">
                                {group.fields.map(renderField)}
                            </div>
                        </div>
                    );
                })}

                {/* gate_names sits with the app-enforced group conceptually but
                    needs a multi-line control, so it gets its own card. */}
                <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-p-4">
                    <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
                        <Icon icon="mdi:gate" className="tw-text-base tw-text-sky-600" />
                        <h2 className="tw-mb-0 tw-text-xs tw-font-bold tw-text-gray-800">Gate names</h2>
                    </div>
                    <label className={labelCls}>One per line</label>
                    <div className={CONTROL_WIDTH.text}>
                        <textarea
                            rows={4}
                            disabled={!canEdit}
                            value={form.gate_names}
                            onChange={e => set('gate_names', e.target.value)}
                            placeholder={'Main Gate\nService Gate\nClub Entrance'}
                            className={`${controlCls(false)} tw-resize-none disabled:tw-bg-gray-50`}
                        />
                    </div>
                    <p className={hintCls}>
                        Recorded for reference. The gate fields on visit, delivery and cab forms accept any text —
                        they are not yet restricted to this list.
                    </p>
                </div>

                {canEdit && (
                    <div className="tw-flex tw-justify-end">
                        <button
                            type="submit"
                            disabled={saving}
                            className="tw-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-2 tw-text-xs tw-font-medium tw-text-white tw-bg-sky-600 hover:tw-bg-sky-700 tw-rounded-md disabled:tw-opacity-50"
                        >
                            <Icon icon="mdi:content-save-outline" className="tw-text-base" />
                            {saving ? 'Saving...' : 'Save Settings'}
                        </button>
                    </div>
                )}
            </form>
        </div>
    );
};

export default VmsSettings;
